import { formatAmount, formatDate } from '@mgonetwork/core';
import { type AllEpochsAddressMetrics } from '@mgonetwork/mango.js/client';
import { Heading, LoadingIndicator, Text } from '@mgonetwork/ui';
import { ParentSize } from '@visx/responsive';
import clsx from 'clsx';
import { useMemo } from 'react';

import { AreaGraph } from './AreaGraph';
import { FormattedStatsAmount } from './HomeMetrics/FormattedStatsAmount';
import { ErrorBoundary } from './error-boundary/ErrorBoundary';
import { useGetAddressMetrics } from '~/hooks/useGetAddressMetrics';
import { useGetAllEpochAddressMetrics } from '~/hooks/useGetAllEpochAddressMetrics';
import { Card } from '~/ui/Card';

import styles from './HomeMetrics/homeMetrics.module.css';
const graphDataField = 'cumulativeAddresses' as const;
const graphDataText = 'Total accounts';

function TooltipContent({ data }: { data: AllEpochsAddressMetrics[number] }) {
	const dateFormatted = formatDate(new Date(data.timestampMs), ['day', 'month']);
	const totalFormatted = formatAmount(data[graphDataField]);
	return (
		<div className="flex flex-col gap-0.5">
			<Text variant="subtitleSmallExtra/medium" color="steel-darker">
				{dateFormatted}, Epoch {data.epoch}
			</Text>
			<Heading variant="heading6/semibold" color="steel-darker">
				{totalFormatted}
			</Heading>
			<Text variant="subtitleSmallExtra/medium" color="steel-darker" uppercase>
				{graphDataText}
			</Text>
		</div>
	);
}

export function AccountsCardGraph({ onClick }: { onClick: () => void }) {
	const { data: addressMetrics } = useGetAddressMetrics();
	const { data: allEpochMetrics, isLoading } = useGetAllEpochAddressMetrics({
		descendingOrder: false,
	});
	const adjEpochAddressMetrics = useMemo(() => allEpochMetrics?.slice(-30), [allEpochMetrics]);
	return (
		<Card
			bg="definedBlack"
			spacing={!adjEpochAddressMetrics?.length ? 'lg' : 'lgGraph'}
			height="full"
		>
			<div className="flex h-full flex-col gap-4 overflow-hidden">
				<Heading variant="heading4/semibold" color="steel-darker">
					<div className="flex cursor-pointer">
						<div className="mr-10" onClick={onClick}>
							Transaction Blocks
						</div>
						<div className="defined-my-text-hero-darkest font-medium">Accounts</div>
					</div>
				</Heading>
				<div className="flex flex-wrap gap-6">
					<div className="flex-1 flex-shrink-0">
						<FormattedStatsAmount
							orientation="vertical"
							label="Total"
							tooltip="Number of accounts that have sent or received transactions since network genesis"
							amount={addressMetrics?.cumulativeAddresses}
							size="md"
						/>
					</div>
					<div className="flex-1 flex-shrink-0">
						<FormattedStatsAmount
							orientation="vertical"
							label="Total Active"
							tooltip="Total number of accounts that have signed transactions since network genesis"
							amount={addressMetrics?.cumulativeActiveAddresses}
							size="md"
						/>
					</div>
					{/* <div className="flex-1 flex-shrink-0">
						<FormattedStatsAmount
							orientation="vertical"
							label="Daily Active"
							tooltip="Number of accounts that have sent or received transactions in the last epoch"
							amount={addressMetrics?.dailyActiveAddresses}
							size="md"
						/>
					</div> */}
				</div>
				<div
					className={clsx(
						'flex min-h-[180px] flex-1 flex-col items-center justify-center rounded-xl transition-colors',
						!adjEpochAddressMetrics?.length && styles.definedCardBlockColor,
					)}
				>
					{isLoading ? (
						<div className="flex flex-col items-center gap-1">
							<LoadingIndicator />
							<Text color="steel" variant="body/medium">
								loading data
							</Text>
						</div>
					) : adjEpochAddressMetrics?.length ? (
						<div className="relative flex-1 self-stretch">
							<ErrorBoundary>
								<ParentSize className="absolute">
									{({ height, width }) => (
										<AreaGraph
											data={adjEpochAddressMetrics}
											height={height}
											width={width}
											getX={({ epoch }) => epoch}
											getY={(data) => data[graphDataField]}
											color="blue"
											formatY={formatAmount}
											tooltipContent={TooltipContent}
										/>
									)}
								</ParentSize>
							</ErrorBoundary>
						</div>
					) : (
						<Text color="steel" variant="body/medium">
							No historical data available
						</Text>
					)}
				</div>
			</div>
		</Card>
	);
}
